import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import { MessageSquare, Plus, Clock, CheckCircle, AlertTriangle, Send, ArrowLeft, XCircle } from 'lucide-react';

const SupportPage = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showNewTicket, setShowNewTicket] = useState(false);
    const [newTicket, setNewTicket] = useState({ subject: '', category: 'general', message: '' });
    const [replyText, setReplyText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { fetchTickets(); }, []);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const res = await api.get('/support/tickets');
            setTickets(res.data.tickets || []);
        } catch (error) {
            console.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        if (!newTicket.subject.trim() || !newTicket.message.trim()) {
            return toast.error('Please fill all fields');
        }
        try {
            setSubmitting(true);
            await api.post('/support/tickets', newTicket);
            toast.success('Ticket created successfully');
            setShowNewTicket(false);
            setNewTicket({ subject: '', category: 'general', message: '' });
            fetchTickets();
        } catch (error) {
            toast.error('Failed to create ticket');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReply = async () => {
        if (!replyText.trim()) return;
        try {
            await api.post(`/support/tickets/${selectedTicket.id}/reply`, { message: replyText });
            toast.success('Reply sent');
            setReplyText('');
            fetchTickets();
            // Update selected ticket messages
            setSelectedTicket(prev => ({
                ...prev,
                messages: [...(prev.messages || []), { sender: 'user', message: replyText, timestamp: new Date() }]
            }));
        } catch (error) {
            toast.error('Failed to send reply');
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            open: 'bg-yellow-500/20 text-yellow-400',
            in_progress: 'bg-blue-500/20 text-blue-400',
            waiting_reply: 'bg-purple-500/20 text-purple-400',
            resolved: 'bg-green-500/20 text-green-400',
            closed: 'bg-gray-500/20 text-gray-400'
        };
        const icons = {
            open: <Clock size={14} />,
            in_progress: <AlertTriangle size={14} />,
            waiting_reply: <MessageSquare size={14} />,
            resolved: <CheckCircle size={14} />,
            closed: <XCircle size={14} />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${styles[status] || styles.open}`}>
                {icons[status]} {status?.replace('_', ' ')}
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col">
            <Navbar />
            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                                <MessageSquare className="text-purple-400" /> Support Center
                            </h1>
                            <p className="text-gray-400 text-sm">Get help with your account</p>
                        </div>
                        {!showNewTicket && !selectedTicket && (
                            <button onClick={() => setShowNewTicket(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">
                                <Plus size={18} /> New Ticket
                            </button>
                        )}
                    </div>

                    {/* New Ticket Form */}
                    {showNewTicket && (
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-white">Create New Ticket</h2>
                                <button onClick={() => setShowNewTicket(false)} className="text-gray-400 hover:text-white">
                                    <ArrowLeft size={20} />
                                </button>
                            </div>
                            <form onSubmit={handleCreateTicket} className="space-y-4">
                                <div>
                                    <label className="text-gray-400 text-sm">Subject</label>
                                    <input type="text" value={newTicket.subject} onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} placeholder="Brief description of your issue" className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" required />
                                </div>
                                <div>
                                    <label className="text-gray-400 text-sm">Category</label>
                                    <select value={newTicket.category} onChange={e => setNewTicket({ ...newTicket, category: e.target.value })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                                        <option value="general">General Inquiry</option>
                                        <option value="deposit">Deposit Issue</option>
                                        <option value="withdrawal">Withdrawal Issue</option>
                                        <option value="trading">Trading Issue</option>
                                        <option value="kyc">KYC Verification</option>
                                        <option value="security">Security Concern</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-gray-400 text-sm">Message</label>
                                    <textarea value={newTicket.message} onChange={e => setNewTicket({ ...newTicket, message: e.target.value })} placeholder="Describe your issue in detail..." className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white h-32" required />
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setShowNewTicket(false)} className="px-4 py-2 bg-slate-700 text-white rounded-lg">Cancel</button>
                                    <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50">
                                        {submitting ? 'Submitting...' : 'Submit Ticket'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Ticket Detail View */}
                    {selectedTicket && !showNewTicket && (
                        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                                <div>
                                    <button onClick={() => setSelectedTicket(null)} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mb-2">
                                        <ArrowLeft size={16} /> Back to tickets
                                    </button>
                                    <h2 className="text-lg font-bold text-white">{selectedTicket.subject}</h2>
                                    <p className="text-gray-400 text-sm">{selectedTicket.category} • {new Date(selectedTicket.createdAt).toLocaleDateString()}</p>
                                </div>
                                {getStatusBadge(selectedTicket.status)}
                            </div>

                            <div className="p-4 h-80 overflow-y-auto space-y-4">
                                {(selectedTicket.messages || []).map((m, i) => (
                                    <div key={i} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] p-3 rounded-lg ${m.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-gray-200'}`}>
                                            <p className="text-xs opacity-70 mb-1">{m.sender === 'admin' ? 'Support Team' : 'You'}</p>
                                            <p>{m.message}</p>
                                            <p className="text-xs opacity-50 mt-1">{new Date(m.timestamp).toLocaleString()}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                                <div className="p-4 border-t border-slate-700">
                                    <div className="flex gap-2">
                                        <input type="text" placeholder="Type your message..." value={replyText} onChange={e => setReplyText(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleReply()} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                                        <button onClick={handleReply} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">
                                            <Send size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tickets List */}
                    {!showNewTicket && !selectedTicket && (
                        <div className="bg-slate-800 rounded-xl border border-slate-700">
                            {loading ? (
                                <div className="text-center py-12 text-gray-400">Loading tickets...</div>
                            ) : tickets.length === 0 ? (
                                <div className="text-center py-16 text-gray-400">
                                    <MessageSquare size={48} className="mx-auto mb-4 text-purple-400/50" />
                                    <p className="text-lg">No support tickets yet</p>
                                    <p className="text-sm">Create a ticket if you need help</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-700">
                                    {tickets.map(ticket => (
                                        <div key={ticket.id} onClick={() => setSelectedTicket(ticket)} className="p-4 hover:bg-slate-700/50 cursor-pointer transition">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-white font-medium">{ticket.subject}</h3>
                                                    <p className="text-gray-400 text-sm">{ticket.category} • {new Date(ticket.createdAt).toLocaleDateString()}</p>
                                                </div>
                                                {getStatusBadge(ticket.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default SupportPage;
